import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listReferrals, getReferral, updateReferral, addReferralFollowUp, getCaregivers } from '../utils/api';
import { PageHeader, Panel, StatCard, StatusPill, Empty, Skeleton, Icon, Pager, useToast } from '../components/ui';

// Clinic Referrals: patient referrals submitted by healthcare professionals through the
// public website portal (Phase 2 of the clinic referral program). Everything here is
// PHI: the page never puts a patient value in the URL, only the opaque reference.

const PAGE_SIZES = [10, 15, 25, 50];
const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'closed', label: 'Closed' },
];
const CLOSURE_REASONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'unable_to_reach', label: 'Unable to reach' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'do_not_contact', label: 'Do Not Contact' },
  { value: 'other_administrative', label: 'Other administrative reason' },
];
const OUTCOMES = [
  { value: 'no_answer', label: 'No answer' },
  { value: 'left_voicemail', label: 'Left voicemail' },
  { value: 'spoke_with_patient', label: 'Spoke with patient' },
  { value: 'spoke_with_family', label: 'Spoke with family' },
  { value: 'callback_requested', label: 'Callback requested' },
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'other', label: 'Other' },
];
const CALLBACK_LABELS = { any: 'Any time', morning: 'Morning', afternoon: 'Afternoon' };
const EVENT_LABELS = {
  created: 'Referral received',
  viewed: 'Viewed',
  assigned: 'Assignment changed',
  status_changed: 'Status changed',
  follow_up_recorded: 'Contact attempt recorded',
  follow_up_scheduled: 'Next follow-up set',
  note_updated: 'Notes updated',
  closed: 'Closed',
  do_not_contact_changed: 'Do Not Contact changed',
};
const labelOf = (list, value) => (list.find(o => o.value === value) || {}).label || value || '';

// All dates on this page are shown in Detroit time regardless of the viewer's device.
const DETROIT = 'America/Detroit';
function fmtDetroit(iso, opts) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { timeZone: DETROIT, ...opts });
}
const fmtDate = (iso) => fmtDetroit(iso, { month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (iso) => fmtDetroit(iso, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
const fmtDateTime = (iso) => fmtDetroit(iso, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

// datetime-local inputs work in the browser's local zone; staff devices are set to Detroit time.
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const fmtPhone = (e164) => {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164 || '');
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : (e164 || '');
};

const LIST_ERROR = 'Unable to load clinic referrals. Please try again.';
const loadError = (e, fallback) => (!e?.message || /failed to fetch/i.test(e.message) ? fallback : e.message);

function CopyButton({ value, label }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch (e) { /* clipboard unavailable: the value is still visible beside the button */ }
  };
  if (!value) return null;
  return (
    <button type="button" className="ops-btn ops-btn--sm ops-btn--ghost ops-copy" onClick={copy} aria-label={`Copy ${label}`} title={`Copy ${label}`}>
      <Icon name={done ? 'check' : 'file'} size={13} /> {done ? 'Copied' : 'Copy'}
    </button>
  );
}

export default function ReferralsPage() {
  const [params, setParams] = useSearchParams();
  const [showToast, toastNode] = useToast();

  // The list filters live in the URL so a filtered view can be reloaded or shared.
  const filters = {
    q: params.get('q') || '',
    status: params.get('status') || '',
    clinic: params.get('clinic') || '',
    assigned: params.get('assigned') || '',
    from: params.get('from') || '',
    to: params.get('to') || '',
    page: Math.max(1, parseInt(params.get('page'), 10) || 1),
    pageSize: PAGE_SIZES.includes(Number(params.get('pageSize'))) ? Number(params.get('pageSize')) : PAGE_SIZES[0],
  };
  const openRef = params.get('ref') || '';
  const filterKey = JSON.stringify(filters);

  const setMany = useCallback((changes, resetPage = true) => {
    setParams(prev => {
      const p = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => { if (v) p.set(k, String(v)); else p.delete(k); });
      if (resetPage) p.delete('page');
      return p;
    }, { replace: true });
  }, [setParams]);

  const [search, setSearch] = useState(filters.q);
  useEffect(() => { setSearch(filters.q); }, [filters.q]);
  useEffect(() => {
    if (search === filters.q) return undefined;
    const t = setTimeout(() => setMany({ q: search.trim() }), 300);
    return () => clearTimeout(t);
  }, [search, filters.q, setMany]);

  const [data, setData] = useState(null);
  const [listError, setListError] = useState('');
  const [tick, setTick] = useState(0);
  const refresh = () => setTick(t => t + 1);

  useEffect(() => {
    let cancelled = false;
    const f = JSON.parse(filterKey);
    (async () => {
      try {
        const d = await listReferrals({ ...f, sort: 'receivedAt', dir: 'desc' });
        if (!cancelled) { setData(d); setListError(''); }
      } catch (e) {
        if (!cancelled) { setListError(loadError(e, LIST_ERROR)); setData(d => d || { items: [], total: 0, clinics: [], assignees: [], metrics: null }); }
      }
    })();
    return () => { cancelled = true; };
  }, [filterKey, tick]);

  const metrics = data?.metrics || null;
  const rows = data?.items || null;
  const total = data?.total || 0;

  // Staff who can be assigned: admins from the existing user directory.
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getCaregivers().then(r => {
      if (cancelled) return;
      const list = (r.users || [])
        .filter(u => u.role === 'admin' && (u.status || '').toUpperCase() !== 'DISABLED')
        .map(u => ({ id: u.sub || u.username, name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email, email: u.email || null }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setStaff(list);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const openReferral = (r) => setMany({ ref: r.referralReference }, false);
  const closeReferral = useCallback(() => setMany({ ref: '' }, false), [setMany]);
  useEffect(() => {
    if (!openRef) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeReferral(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openRef, closeReferral]);

  const assigneeOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    (data?.assignees || []).concat(staff).forEach(a => { if (a?.id && !seen.has(a.id)) { seen.add(a.id); out.push(a); } });
    return out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [data, staff]);

  const hasFilters = filters.q || filters.status || filters.clinic || filters.assigned || filters.from || filters.to;

  return (
    <main className="main-content ops-main">
      <div className="ops-page">
        <PageHeader title="Clinic Referrals"
          subtitle="Manage patient referrals submitted by healthcare professionals and track follow-up activity."
          actions={<button type="button" className="ops-btn" onClick={refresh}><Icon name="refresh" /> Refresh</button>} />

        <div className="ops-kpis ops-kpis--4">
          <StatCard label="New Referrals" value={metrics ? metrics.new : '…'} tone="amber" sub="Awaiting first contact" />
          <StatCard label="In Progress" value={metrics ? metrics.in_progress : '…'} tone="blue" sub="Contact attempts under way" />
          <StatCard label="Contacted" value={metrics ? metrics.contacted : '…'} tone="green" sub="Patient or family reached" />
          <StatCard label="Closed" value={metrics ? metrics.closed : '…'} sub={metrics ? `${metrics.total} total received` : ''} />
        </div>

        {listError && <div className="ops-error" role="alert">{listError}</div>}

        <Panel flush title="Referrals" count={rows ? total : undefined}
          foot={rows && total > 0 ? (
            <Pager page={filters.page} pageSize={filters.pageSize} total={total} sizes={PAGE_SIZES} label="referrals"
              onPage={p => setMany({ page: p > 1 ? p : '' }, false)}
              onPageSize={s => setMany({ pageSize: s !== PAGE_SIZES[0] ? s : '' })} />
          ) : null}>
          <div className="ops-filters ops-filters--embedded">
            <div className="ops-search">
              <Icon name="search" />
              <input className="ops-input" type="search" placeholder="Search reference, patient or clinic" value={search}
                onChange={e => setSearch(e.target.value)} aria-label="Search referrals" />
            </div>
            <select className="ops-select" value={filters.status} onChange={e => setMany({ status: e.target.value })} aria-label="Status">
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select className="ops-select" value={filters.clinic} onChange={e => setMany({ clinic: e.target.value })} aria-label="Clinic">
              <option value="">All clinics</option>
              {(data?.clinics || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="ops-select" value={filters.assigned} onChange={e => setMany({ assigned: e.target.value })} aria-label="Assigned staff">
              <option value="">Any staff</option>
              <option value="unassigned">Unassigned</option>
              {assigneeOptions.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <label className="ops-field ops-field--inline ops-referral-date"><span className="ops-label">From</span>
              <input className="ops-input" type="date" value={filters.from} max={filters.to || undefined} onChange={e => setMany({ from: e.target.value })} aria-label="Received from" /></label>
            <label className="ops-field ops-field--inline ops-referral-date"><span className="ops-label">To</span>
              <input className="ops-input" type="date" value={filters.to} min={filters.from || undefined} onChange={e => setMany({ to: e.target.value })} aria-label="Received to" /></label>
            {hasFilters && <button type="button" className="ops-btn ops-btn--sm ops-btn--ghost" onClick={() => { setSearch(''); setMany({ q: '', status: '', clinic: '', assigned: '', from: '', to: '' }); }}>Clear</button>}
          </div>

          {!rows ? <Skeleton rows={6} /> : rows.length === 0 ? (
            <Empty title={hasFilters ? 'No referrals match these filters' : undefined}>
              {hasFilters ? 'Try widening the date range or clearing a filter.' : 'No clinic referrals have been received yet.'}
            </Empty>
          ) : (
            <div className="ops-table-wrap">
              <table className="ops-table ops-referrals-table">
                <thead><tr>
                  <th>Referral Reference</th><th>Date Received</th><th>Clinic / Organization</th><th>Patient Name</th>
                  <th>Patient Telephone</th><th>Preferred Callback Time</th><th>Status</th><th>Assigned Staff</th><th><span className="ops-sr-only">Actions</span></th>
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.referralId} className={`is-clickable${openRef === r.referralReference ? ' is-selected' : ''}`} onClick={() => openReferral(r)}>
                      <td data-label="Reference"><span className="mono">{r.referralReference}</span>{r.doNotContact && <div className="ops-cell-sub neg">Do Not Contact</div>}</td>
                      <td data-label="Received"><div className="ops-cell-main">{fmtDate(r.receivedAt)}</div><div className="ops-cell-sub">{fmtTime(r.receivedAt)}</div></td>
                      <td data-label="Clinic"><div className="ops-cell-main">{r.clinicName}</div><div className="ops-cell-sub">{r.contactPersonName}</div></td>
                      <td data-label="Patient" className="ops-cell-main">{r.patientName}</td>
                      <td data-label="Telephone" className="nowrap">{fmtPhone(r.patientPhone)}</td>
                      <td data-label="Callback">{CALLBACK_LABELS[r.preferredCallbackTime] || r.preferredCallbackTime}</td>
                      <td data-label="Status"><StatusPill status={r.status} /></td>
                      <td data-label="Assigned">{r.assignedTo?.name || <span className="muted">Unassigned</span>}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <button type="button" className="ops-btn ops-btn--sm" onClick={() => openReferral(r)}>Open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {openRef && (
        <ReferralDrawer reference={openRef} staff={staff} onClose={closeReferral}
          onChanged={() => { refresh(); }} toast={showToast} />
      )}
      {toastNode}
    </main>
  );
}

function ReferralDrawer({ reference, staff, onClose, onChanged, toast }) {
  const [detail, setDetail] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [notes, setNotes] = useState('');
  const [nextFollowUp, setNextFollowUp] = useState('');
  const [followUp, setFollowUp] = useState({ outcome: '', note: '', nextFollowUpAt: '' });

  const load = useCallback(async () => {
    try {
      const d = await getReferral(reference);
      setDetail(d.referral);
      setEvents(d.events || []);
      setNotes(d.referral.notes || '');
      setNextFollowUp(toLocalInput(d.referral.nextFollowUpAt));
      setError('');
    } catch (e) {
      setError(e?.status === 404 || /not found/i.test(e?.message || '') ? 'This referral could not be found.' : loadError(e, 'Unable to load this referral. Please try again.'));
    }
  }, [reference]);
  useEffect(() => { setDetail(null); load(); }, [load]);

  const apply = async (label, fn, okMsg) => {
    setBusy(label);
    try {
      const r = await fn();
      if (r?.referral) { setDetail(r.referral); setNotes(r.referral.notes || ''); setNextFollowUp(toLocalInput(r.referral.nextFollowUpAt)); }
      await load();
      onChanged();
      if (okMsg) toast(okMsg);
      return true;
    } catch (e) {
      toast(loadError(e, 'The change could not be saved. Please try again.'), 'error');
      return false;
    } finally { setBusy(''); }
  };

  const assignOptions = useMemo(() => {
    const list = [...staff];
    if (detail?.assignedTo?.id && !list.some(s => s.id === detail.assignedTo.id)) list.push(detail.assignedTo);
    return list;
  }, [staff, detail]);

  const setAssignee = (id) => {
    const assignee = id ? assignOptions.find(s => s.id === id) : null;
    if (id && !assignee) return;
    apply('assign', () => updateReferral(reference, { assignedTo: assignee ? { id: assignee.id, name: assignee.name, email: assignee.email || null } : null }), assignee ? `Assigned to ${assignee.name}` : 'Assignment cleared');
  };
  const setStatus = (status) => {
    if (!status || status === detail.status || status === 'closed') return;
    apply('status', () => updateReferral(reference, { status }), `Status set to ${labelOf(STATUS_OPTIONS, status)}`);
  };
  const closeOut = () => {
    if (!closeReason) { toast('Choose a closure reason first.', 'error'); return; }
    if (closeReason === 'do_not_contact' && !window.confirm('Close this referral and mark the patient Do Not Contact? No further contact attempts can be recorded.')) return;
    apply('close', () => updateReferral(reference, { status: 'closed', closureReason: closeReason }), 'Referral closed');
  };
  const toggleDnc = () => {
    const next = !detail.doNotContact;
    const msg = next
      ? 'Mark this patient Do Not Contact? Staff will be blocked from recording contact attempts until it is cleared.'
      : 'Clear the Do Not Contact flag for this patient?';
    if (!window.confirm(msg)) return;
    apply('dnc', () => updateReferral(reference, { doNotContact: next }), next ? 'Marked Do Not Contact' : 'Do Not Contact cleared');
  };
  const saveNotes = () => apply('notes', () => updateReferral(reference, { notes }), 'Notes saved');
  const saveNextFollowUp = () => apply('next', () => updateReferral(reference, { nextFollowUpAt: fromLocalInput(nextFollowUp) }), 'Next follow-up saved');
  const recordFollowUp = (e) => {
    e.preventDefault();
    if (!followUp.outcome) { toast('Choose an outcome first.', 'error'); return; }
    const body = { outcome: followUp.outcome, note: followUp.note.trim() };
    if (followUp.nextFollowUpAt) body.nextFollowUpAt = fromLocalInput(followUp.nextFollowUpAt);
    apply('follow', () => addReferralFollowUp(reference, body), 'Contact attempt recorded').then(ok => { if (ok) setFollowUp({ outcome: '', note: '', nextFollowUpAt: '' }); });
  };

  const closed = detail?.status === 'closed';
  const dnc = detail?.doNotContact === true;
  const notesDirty = detail && notes.trim() !== (detail.notes || '');
  const nextDirty = detail && (fromLocalInput(nextFollowUp) || null) !== (detail.nextFollowUpAt || null);

  return (
    <>
      <div className="ops-drawer__backdrop" onClick={onClose} />
      <aside className="ops-drawer ops-drawer--wide" role="dialog" aria-modal="true" aria-label="Clinic referral details">
        <div className="ops-drawer__head">
          <div>
            <div className="ops-label">Clinic referral</div>
            <h2 style={{ margin: '4px 0 2px' }} className="mono">{reference}</h2>
            <div className="ops-cell-sub">{detail ? `Received ${fmtDateTime(detail.receivedAt)} (Detroit time)` : ''}</div>
          </div>
          <div className="ops-actions">
            {detail && <StatusPill status={detail.status} />}
            <button type="button" className="ops-close" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
          </div>
        </div>
        <div className="ops-drawer__body">
          {error && <div className="ops-error" role="alert">{error}</div>}
          {!detail && !error && <Skeleton rows={8} />}
          {detail && (
            <>
              {dnc && <div className="ops-note ops-note--review"><strong>Do Not Contact.</strong> This patient has asked not to be contacted. No contact attempts can be recorded while this flag is set.</div>}

              <section className="ops-form-section">
                <h3>Referral information</h3>
                <dl className="ops-dl ops-dl--4">
                  <div><dt>Reference</dt><dd className="mono">{detail.referralReference}</dd></div>
                  <div><dt>Submitted</dt><dd>{fmtDate(detail.submittedAt)}<div className="ops-cell-sub">{fmtTime(detail.submittedAt)}</div></dd></div>
                  <div><dt>Source</dt><dd>{detail.sourceLabel || 'Clinic Referral Portal'}</dd></div>
                  <div><dt>Status</dt><dd><StatusPill status={detail.status} />{closed && detail.closureReason && <div className="ops-cell-sub">{labelOf(CLOSURE_REASONS, detail.closureReason)}{detail.closedAt ? `, ${fmtDateTime(detail.closedAt)}` : ''}</div>}</dd></div>
                  <div><dt>Assigned staff</dt><dd>{detail.assignedTo?.name || <span className="muted">Unassigned</span>}</dd></div>
                  <div><dt>Contact attempts</dt><dd>{detail.contactAttemptCount}{detail.lastContactAttemptAt && <div className="ops-cell-sub">Last {fmtDateTime(detail.lastContactAttemptAt)}</div>}</dd></div>
                  <div><dt>Last outcome</dt><dd>{detail.lastContactOutcome ? labelOf(OUTCOMES, detail.lastContactOutcome) : <span className="muted">None yet</span>}</dd></div>
                  <div><dt>Next follow-up</dt><dd>{detail.nextFollowUpAt ? fmtDateTime(detail.nextFollowUpAt) : <span className="muted">Not scheduled</span>}</dd></div>
                </dl>
              </section>

              <section className="ops-form-section">
                <h3>Healthcare professional</h3>
                <dl className="ops-dl ops-dl--4">
                  <div><dt>Clinic / organization</dt><dd>{detail.clinicName}</dd></div>
                  <div><dt>Contact person</dt><dd>{detail.contactPersonName}</dd></div>
                  <div><dt>Office email</dt><dd><a className="ops-link" href={`mailto:${detail.officeEmail}`}>{detail.officeEmail}</a> <CopyButton value={detail.officeEmail} label="office email" /></dd></div>
                  <div><dt>Office phone</dt><dd>{detail.officePhone ? <><a className="ops-link" href={`tel:${detail.officePhone}`}>{fmtPhone(detail.officePhone)}</a> <CopyButton value={detail.officePhone} label="office phone" /></> : <span className="muted">Not provided</span>}</dd></div>
                </dl>
              </section>

              <section className="ops-form-section">
                <h3>Patient contact</h3>
                <dl className="ops-dl ops-dl--4">
                  <div><dt>Patient name</dt><dd>{detail.patientName} <CopyButton value={detail.patientName} label="patient name" /></dd></div>
                  <div><dt>Patient telephone</dt><dd>{dnc ? <span>{fmtPhone(detail.patientPhone)}</span> : <a className="ops-link" href={`tel:${detail.patientPhone}`}>{fmtPhone(detail.patientPhone)}</a>} <CopyButton value={detail.patientPhone} label="patient telephone" /></dd></div>
                  <div><dt>Preferred callback time</dt><dd>{CALLBACK_LABELS[detail.preferredCallbackTime] || detail.preferredCallbackTime}</dd></div>
                </dl>
              </section>

              <section className="ops-form-section">
                <h3>Authorization attestation</h3>
                <dl className="ops-dl ops-dl--4">
                  <div><dt>Authorization confirmed</dt><dd>{detail.authorizationConfirmed ? 'Yes' : 'No'}</dd></div>
                  <div><dt>Attested</dt><dd>{fmtDateTime(detail.authorizationAttestedAt)}</dd></div>
                  <div><dt>Attestation version</dt><dd>{detail.authorizationAttestationVersion}</dd></div>
                </dl>
                <p className="ops-note" style={{ marginTop: 10 }}>The referring healthcare organization confirmed that it obtained and retained the required written patient authorization. The signed authorization is maintained by the referring organization.</p>
              </section>

              <section className="ops-form-section">
                <h3>Follow-up</h3>
                <div className="ops-form-grid">
                  <div className="ops-field">
                    <label htmlFor="ref-assign">Assigned staff</label>
                    <select id="ref-assign" className="ops-select" value={detail.assignedTo?.id || ''} disabled={!!busy} onChange={e => setAssignee(e.target.value)}>
                      <option value="">Unassigned</option>
                      {assignOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="ops-field">
                    <label htmlFor="ref-status">Status</label>
                    <select id="ref-status" className="ops-select" value={detail.status} disabled={!!busy} onChange={e => setStatus(e.target.value)}>
                      {STATUS_OPTIONS.filter(o => o.value !== 'closed').map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      {closed && <option value="closed">Closed</option>}
                    </select>
                    {closed && <span className="ops-field__hint">Choose another status to reopen this referral.</span>}
                  </div>
                  <div className="ops-field">
                    <label htmlFor="ref-next">Next follow-up</label>
                    <div className="ops-actions">
                      <input id="ref-next" className="ops-input" type="datetime-local" value={nextFollowUp} disabled={!!busy || closed} onChange={e => setNextFollowUp(e.target.value)} />
                      <button type="button" className="ops-btn ops-btn--sm" disabled={!!busy || closed || !nextDirty} onClick={saveNextFollowUp}>Save</button>
                    </div>
                  </div>
                  <div className="ops-field">
                    <label htmlFor="ref-dnc">Do Not Contact</label>
                    <div className="ops-actions">
                      <button id="ref-dnc" type="button" className={`ops-btn ops-btn--sm${dnc ? '' : ' ops-btn--danger'}`} disabled={!!busy} onClick={toggleDnc} aria-pressed={dnc}>
                        {dnc ? 'Clear Do Not Contact' : 'Mark Do Not Contact'}
                      </button>
                    </div>
                  </div>
                  <div className="ops-field span-2">
                    <label htmlFor="ref-notes">Operational notes <span className="ops-field__hint">Scheduling and contact details only. No diagnoses, medications or insurance.</span></label>
                    <textarea id="ref-notes" className="ops-input" rows={3} maxLength={1000} value={notes} disabled={!!busy} onChange={e => setNotes(e.target.value)} />
                    <div className="ops-actions"><button type="button" className="ops-btn ops-btn--sm" disabled={!!busy || !notesDirty} onClick={saveNotes}>Save notes</button></div>
                  </div>
                </div>
              </section>

              {!closed && (
                <section className="ops-form-section">
                  <h3>Record a contact attempt</h3>
                  {dnc ? <div className="ops-note">Contact attempts cannot be recorded while Do Not Contact is set.</div> : (
                    <form className="ops-form-grid" onSubmit={recordFollowUp}>
                      <div className="ops-field">
                        <label htmlFor="ref-outcome">Outcome</label>
                        <select id="ref-outcome" className="ops-select" value={followUp.outcome} disabled={!!busy} onChange={e => setFollowUp(f => ({ ...f, outcome: e.target.value }))} required>
                          <option value="">Choose an outcome</option>
                          {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <div className="ops-field">
                        <label htmlFor="ref-fu-next">Next follow-up (optional)</label>
                        <input id="ref-fu-next" className="ops-input" type="datetime-local" value={followUp.nextFollowUpAt} disabled={!!busy} onChange={e => setFollowUp(f => ({ ...f, nextFollowUpAt: e.target.value }))} />
                      </div>
                      <div className="ops-field span-2">
                        <label htmlFor="ref-fu-note">Note (optional)</label>
                        <input id="ref-fu-note" className="ops-input" type="text" maxLength={500} placeholder="Short note about this attempt" value={followUp.note} disabled={!!busy} onChange={e => setFollowUp(f => ({ ...f, note: e.target.value }))} />
                      </div>
                      <div className="span-2 ops-actions">
                        <button type="submit" className="ops-btn ops-btn--primary" disabled={!!busy}>{busy === 'follow' ? 'Saving…' : 'Record attempt'}</button>
                        <span className="muted" style={{ fontSize: 12.5 }}>Speaking with the patient or family sets the status to Contacted. Other outcomes move a new referral to In progress.</span>
                      </div>
                    </form>
                  )}
                </section>
              )}

              {!closed && (
                <section className="ops-form-section">
                  <h3>Close referral</h3>
                  <div className="ops-form-grid">
                    <div className="ops-field">
                      <label htmlFor="ref-close-reason">Closure reason</label>
                      <select id="ref-close-reason" className="ops-select" value={closeReason} disabled={!!busy} onChange={e => setCloseReason(e.target.value)}>
                        <option value="">Choose a reason</option>
                        {CLOSURE_REASONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="ops-field"><label>&nbsp;</label>
                      <div className="ops-actions"><button type="button" className="ops-btn ops-btn--danger" disabled={!!busy || !closeReason} onClick={closeOut}>Close referral</button></div>
                    </div>
                  </div>
                </section>
              )}

              <section className="ops-form-section">
                <h3>History</h3>
                {events.length === 0 ? <div className="muted" style={{ fontSize: 13 }}>No activity recorded.</div> : (
                  <ol className="ops-history">
                    {events.map(ev => (
                      <li key={ev.eventId}>
                        <div className="ops-history__when">{fmtDateTime(ev.at)}</div>
                        <div className="ops-history__what">
                          <strong>{EVENT_LABELS[ev.type] || ev.type}</strong>
                          {ev.type === 'status_changed' || ev.type === 'closed' ? <span> {labelOf(STATUS_OPTIONS, ev.previousStatus)} to {labelOf(STATUS_OPTIONS, ev.newStatus)}{ev.closureReason ? ` (${labelOf(CLOSURE_REASONS, ev.closureReason)})` : ''}</span> : null}
                          {ev.type === 'assigned' ? <span> {ev.assignedTo?.name || 'Unassigned'}</span> : null}
                          {ev.type === 'follow_up_recorded' ? <span> {labelOf(OUTCOMES, ev.outcome)}{ev.note ? `: ${ev.note}` : ''}</span> : null}
                          {ev.type === 'follow_up_scheduled' ? <span> {ev.nextFollowUpAt ? fmtDateTime(ev.nextFollowUpAt) : 'cleared'}</span> : null}
                          {ev.type === 'do_not_contact_changed' ? <span> {ev.doNotContact ? 'set' : 'cleared'}</span> : null}
                          <div className="ops-cell-sub">{ev.by?.name || ev.by?.email || (ev.type === 'created' ? 'Clinic Referral Portal' : 'System')}</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </>
          )}
        </div>
        <div className="ops-drawer__foot">
          <span className="muted">Every view and change is recorded in the audit trail.</span>
          <span className="ops-actions">
            <button type="button" className="ops-btn" onClick={onClose}>Close</button>
          </span>
        </div>
      </aside>
    </>
  );
}
